import { attempt } from "@banjoanton/utils";
import { Chess, type Square } from "chess.js";
import { type ChessColor, ChessPiece } from "../../../../models/base-model";
import type { Move } from "../../../../models/move-model";
import { BoardQuery } from "../../../../utils/board-query";
import { EvaluationComparerUtil } from "../../../../utils/evaluation-comparer-util";
import { ExchangeUtil } from "../../../../utils/exchange-util";
import { ForkUtil } from "../../../../utils/fork-util";
import { MoveAnnotation } from "../../models/move-annotation";
import type { CalculateAnnotationFunction } from "../annotation-service";

const EVAL_DEPTH = 10;

/**
 * Check if all legal moves result in the moving piece being attacked.
 * If so, the player has no choice but to "sacrifice" - it's not a real sacrifice.
 */
const isForcedSacrifice = (fenBefore: string): boolean => {
    const board = new Chess(fenBefore);
    const legalMoves = board.moves({ verbose: true });

    for (const move of legalMoves) {
        const tempBoard = new Chess(fenBefore);
        tempBoard.move(move);

        const opponentColor = move.color === "w" ? "b" : "w";
        const attackers = tempBoard.attackers(move.to, opponentColor);

        if (attackers.length === 0) {
            return false;
        }
    }

    return true;
};

type SacrificeData = {
    sacrificedPiece: ChessPiece;
    sacrificedSquare: Square;
    capturedPiece: ChessPiece | undefined;
    attackers: Square[];
    materialLost: number;
};

type CanRecoverMaterialParams = {
    sacrificeData: SacrificeData;
    fenAfter: string;
    colorThatMoved: ChessColor;
};

/**
 * Check if material can be recovered the next turn after opponent captures the sacrificed piece.
 * If we can recapture equal value and material lost is below threshold, it's not a real sacrifice.
 */
const canRecoverMaterialNextTurn = ({
    sacrificeData,
    fenAfter,
    colorThatMoved,
}: CanRecoverMaterialParams): boolean => {
    const lostMaterial = sacrificeData.materialLost;

    for (const attackerSquare of sacrificeData.attackers) {
        const tempEngine = new Chess(fenAfter);
        const successfulAttack = attempt(() =>
            tempEngine.move({
                from: attackerSquare,
                to: sacrificeData.sacrificedSquare,
            })
        );

        if (!successfulAttack) {
            continue;
        }

        const tempQuery = BoardQuery.from(tempEngine);

        // Find opponent pieces we can attack (excluding the sacrificed square)
        // After opponent captures, it's our turn, so opponent pieces are lastMoved color
        const opponentColor = tempQuery.lastMoved;
        const possibleOtherTargets = tempQuery
            .pieces(opponentColor)
            .filter((p) => p.square !== sacrificeData.sacrificedSquare)
            .filter((p) => tempQuery.attackers(p.square).length > 0);

        if (possibleOtherTargets.length === 0) {
            continue;
        }

        for (const target of possibleOtherTargets) {
            const attackers = tempQuery.attackers(target.square);
            const defenders = tempQuery.defenders(target.square);

            const seeResult = ExchangeUtil.calculateExchange({
                target: target.type,
                attackers: attackers.map((a) => a.type),
                defenders: defenders.map((d) => d.type),
            });

            const lostMaterialThreshold = 3;
            const isBelowThreshold = lostMaterial <= lostMaterialThreshold;
            const canRecaptureNextTurn = seeResult >= lostMaterial;

            if (isBelowThreshold && canRecaptureNextTurn) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Detect immediate sacrifice, the piece that just moved
 */
const detectImmediateSacrifice = (
    move: Move,
    query: BoardQuery
): SacrificeData | undefined => {
    const captureSquare = move.to as Square;

    const attackers = query.attackers(captureSquare);
    const defenders = query.defenders(captureSquare);

    if (attackers.length === 0) {
        return undefined;
    }

    // Check if opponent can actually capture on the square, might be pinned piece
    let opponentCanAttack = false;
    for (const attacker of attackers) {
        const tempEngine = new Chess(query.fen);
        const successfulAttack = attempt(() =>
            tempEngine.move({
                from: attacker.square,
                to: captureSquare,
            })
        );

        if (successfulAttack) {
            opponentCanAttack = true;
            break;
        }
    }

    if (!opponentCanAttack) {
        return undefined;
    }

    const seeResult = ExchangeUtil.calculateExchange({
        target: move.piece,
        attackers: attackers.map((a) => a.type),
        defenders: defenders.map((d) => d.type),
    });

    const capturedPiece = ChessPiece.parse(move.captured);

    const capturedValue = capturedPiece ? ChessPiece.value(capturedPiece) : 0;
    const netMaterial = capturedValue - seeResult;

    // Not a sacrifice if we gain or break even
    if (netMaterial >= 0) {
        return undefined;
    }

    return {
        sacrificedPiece: move.piece,
        sacrificedSquare: captureSquare,
        capturedPiece,
        materialLost: Math.abs(netMaterial),
        attackers: attackers.map((a) => a.square),
    };
};

/**
 * Detect delayed sacrifice (leaving pieces hanging after non-capture moves)
 */
const detectDelayedSacrifice = (
    move: Move,
    query: BoardQuery
): SacrificeData | undefined => {
    const colorThatMoved = query.lastMoved;

    // do not consider the piece that just moved
    const ourPieces = query
        .pieces(colorThatMoved)
        .filter((p) => p.square !== move.to);

    let maxMaterialAtRisk = 0;
    let worstSacrifice: SacrificeData | undefined = undefined;

    for (const pieceData of ourPieces) {
        const attackers = query.attackers(pieceData.square);
        const defenders = query.defenders(pieceData.square);

        if (attackers.length === 0) {
            continue;
        }

        let opponentCanAttack = false;
        for (const attacker of attackers) {
            const tempEngine = new Chess(query.fen);
            const successfulAttack = attempt(() =>
                tempEngine.move({
                    from: attacker.square,
                    to: pieceData.square,
                })
            );

            if (successfulAttack) {
                opponentCanAttack = true;
                break;
            }
        }

        if (!opponentCanAttack) {
            continue;
        }

        const valueOfCapture = move.captured
            ? ChessPiece.value(ChessPiece.parse(move.captured)!)
            : 0;

        const seeResult = ExchangeUtil.calculateExchange({
            target: pieceData.type,
            attackers: attackers.map((a) => a.type),
            defenders: defenders.map((d) => d.type),
        });

        const seeResultWithCapture = seeResult - valueOfCapture;

        // If opponent gains material by capturing, we're offering a sacrifice
        if (
            seeResultWithCapture > 0 &&
            seeResultWithCapture > maxMaterialAtRisk
        ) {
            maxMaterialAtRisk = seeResultWithCapture;
            worstSacrifice = {
                sacrificedPiece: pieceData.type,
                sacrificedSquare: pieceData.square,
                capturedPiece: undefined, // Delayed sacrifice has no capture yet
                materialLost: seeResultWithCapture,
                attackers: attackers.map((a) => a.square),
            };
        }
    }

    return worstSacrifice;
};

const calculate: CalculateAnnotationFunction = async ({
    move,
    previousMoves,
    evaluator,
}) => {
    const query = BoardQuery.fromMove(move);
    const colorThatMoved = query.lastMoved;
    const nextMoveColor = query.turn;

    if (isForcedSacrifice(move.fenBefore)) {
        return [];
    }

    // Only the moving piece can be considered a sacrifice when it checks the king
    const king = query.findKing(nextMoveColor);

    const attackersOnKing = query.attackers(king.square).map((a) => a.square);
    const moverAttacksKing = attackersOnKing.includes(move.to as Square);
    if (query.inCheck() && !moverAttacksKing) {
        return [];
    }

    // Skip if king moved out of check
    const isKingMove = move.piece === "k";
    const wasCheck = new Chess(move.fenBefore).inCheck();
    if (isKingMove && wasCheck) {
        return [];
    }

    // Skip if piece that moved was in fork targets (correct?)
    const forkedSquares = ForkUtil.getSquaresAttackedByFork(previousMoves);
    if (forkedSquares.includes(move.from)) {
        return [];
    }

    let sacrificeData = detectImmediateSacrifice(move, query);

    // delayed sacrifices are not possible if we check the king
    if (!sacrificeData && !query.inCheck()) {
        sacrificeData = detectDelayedSacrifice(move, query);
    }

    if (!sacrificeData) {
        return [];
    }

    if (
        canRecoverMaterialNextTurn({
            sacrificeData,
            fenAfter: move.fenAfter,
            colorThatMoved,
        })
    ) {
        return [];
    }

    const evalBefore = await evaluator.evaluatePosition(
        move.fenBefore,
        EVAL_DEPTH
    );
    const evalAfter = await evaluator.evaluatePosition(
        move.fenAfter,
        EVAL_DEPTH
    );

    const comparison = EvaluationComparerUtil.compareScore({
        oldScore: evalBefore.evaluationMessage.score,
        newScore: evalAfter.evaluationMessage.score,
        side: colorThatMoved,
        threshold: 0,
    });

    return [
        MoveAnnotation.from({
            type: "sacrifice",
            sacrificedPiece: sacrificeData.sacrificedPiece,
            sacrificedSquare: sacrificeData.sacrificedSquare,
            capturedPiece: sacrificeData.capturedPiece,
            isGambit: sacrificeData.sacrificedPiece === "p",
            materialLost: sacrificeData.materialLost,
            evalImprovement: comparison.diff,
        }),
    ];
};

export const SacrificeAnnotationService = { calculate };
